// Helper for handling API errors in a polymorphic way
import {toast} from 'react-toastify'

// Define error handler interface
class ErrorHandler {
  constructor(options = {}) {
    this.options = options
  }

  handle(error) {
    console.error(`${this.options.operation || 'API'} error:`, error)

    if (!this.options.silent) {
      this.showError(error)
    }

    // Call custom handler if provided
    if (typeof this.options.onError === 'function') {
      this.options.onError(error)
    }
  }

  showError(error) {
    toast.error('An unexpected error occurred')
  }
}

// Handler for 400 status errors
class BadRequestErrorHandler extends ErrorHandler {
  showError(error) {
    const errorMessage = error?.response?.data?.message || 'Invalid request'

    if (errorMessage.includes('customer') || errorMessage.includes('Customer')) {
      toast.error('There was an issue with your customer account. Please contact support.')
    } else if (errorMessage.includes('subscription')) {
      toast.error('Unable to complete subscription operation. Please try again or contact support.')
    } else {
      toast.error(errorMessage)
    }
  }
}

// Handler for 500 status errors
class ServerErrorHandler extends ErrorHandler {
  showError() {
    toast.error('Server error occurred. Please try again later or contact support.')
  }
}

// Handler for network errors
class NetworkErrorHandler extends ErrorHandler {
  showError() {
    toast.error('Network error. Please check your connection and try again.')
  }
}

// Handler for default error cases
class DefaultErrorHandler extends ErrorHandler {
  showError(error) {
    const fallbackMessage = this.options.fallbackMessage || 'Operation failed. Please try again.'
    toast.error(error?.response?.data?.message || fallbackMessage)
  }
}

// Factory to create appropriate error handler
export const createErrorHandler = (error, options = {}) => {
  const handlerOptions = {...options}

  if (error?.response?.status === 400) {
    return new BadRequestErrorHandler(handlerOptions)
  } else if (error?.response?.status === 500) {
    return new ServerErrorHandler(handlerOptions)
  } else if (error?.code === 'NETWORK_ERROR' || !error?.response) {
    return new NetworkErrorHandler(handlerOptions)
  } else {
    return new DefaultErrorHandler(handlerOptions)
  }
}

// Utility function to handle errors with polymorphism
export const handleApiError = (error, options = {}) => {
  // Allow passing just a string for the operation name for backward compatibility
  const handlerOptions = typeof options === 'string' ? {operation: options} : options

  const errorHandler = createErrorHandler(error, handlerOptions)
  errorHandler.handle(error)
}
